import { useState, useCallback } from "react";
import type { SourceSnapshot } from "@tuit/shared-types";
import { formatAge } from "../utils/dateFormat.js";
import { getFaviconUrl } from "../utils/favicon.js";
import { ItemRow } from "./ItemRow.js";
import { useViewSettings } from "../state/viewSettingsState.js";
import styles from "../styles/components/SectionView.module.css";

interface Props {
  section: SourceSnapshot;
  readIds: Set<string>;
  onRead: (id: string) => void;
  suppressed: Set<string>;
  alsoIn: Map<string, string[]>;
  sectionRef: (el: HTMLElement | null) => void;
  highlight?: string;
  mobile?: boolean;
}

export function SectionView({ section, readIds, onRead, suppressed, alsoIn, sectionRef, highlight, mobile = false }: Props) {
  const { hideRead } = useViewSettings();
  const [reporting, setReporting] = useState(false);
  const [reported, setReported] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [dismissingIds, setDismissingIds] = useState<Set<string>>(new Set());

  const handleRead = useCallback((id: string) => {
    if (!hideRead) { onRead(id); return; }
    setDismissingIds((prev) => new Set([...prev, id]));
  }, [hideRead, onRead]);

  async function reportIssue() {
    setReporting(true);
    try {
      await fetch("/api/report-issue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sourceId: section.sourceId, errorMessage: section.errorMessage }),
      });
      setReported(true);
    } finally {
      setReporting(false);
    }
  }

  const visibleItems = section.items.filter((item) => {
    if (suppressed.has(item.id)) return false;
    if (hideRead && readIds.has(item.id)) return false;
    return true;
  });

  const newCount = section.items.filter(
    (item) => !suppressed.has(item.id) && !readIds.has(item.id)
  ).length;

  return (
    <section
      className={styles.section}
      data-section-id={section.sourceId}
      data-collapsed={collapsed}
      ref={sectionRef}
      id={`section-${section.sourceId}`}
    >
      <div
        className={styles.header}
        data-drag-header=""
        onClick={() => setCollapsed((c) => !c)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" || e.key === " " ? setCollapsed((c) => !c) : undefined}
        aria-label={collapsed ? "Expand section" : "Collapse section"}
      >
        <span className={styles.dragHandle} onClick={(e) => e.stopPropagation()}>⠿</span>
        <span className={`${styles.toggle} ${collapsed ? styles.toggleCollapsed : ""}`}>▾</span>
        <div className={styles.headerLeft}>
          {(section.faviconUrl || section.items[0]?.domain) && (
            <span
              className={styles.faviconWrap}
              style={section.iconBackground ? { background: section.iconBackground } : undefined}
            >
              <img
                src={section.faviconUrl ?? getFaviconUrl(section.items[0]?.domain ?? "")}
                className={styles.favicon}
                width={16}
                height={16}
                alt=""
                aria-hidden="true"
              />
            </span>
          )}
          <h2 className={styles.title}>{section.title}</h2>
          {newCount > 0 && <span className={styles.newCount}>{newCount} new</span>}
        </div>
        <span className={styles.refreshed}>updated {formatAge(section.fetchedAt)}</span>
      </div>

      <div className={`${styles.collapse} ${collapsed ? "" : styles.collapseOpen}`}>
        <div className={styles.collapseInner}>
          <div className={styles.items}>
            {visibleItems.length === 0 ? (
              <div className={`${styles.empty} ${section.fetchStatus === "error" ? styles.emptyError : ""}`}>
                {section.fetchStatus === "error" ? "There was an error loading this feed." : "No unread items"}
              </div>
            ) : (
              visibleItems.map((item) => (
                <div
                  key={item.id}
                  className={`${styles.itemWrapper} ${dismissingIds.has(item.id) ? styles.itemDismissing : ""}`}
                  onTransitionEnd={(e) => {
                    if (e.propertyName === "grid-template-rows" && dismissingIds.has(item.id)) {
                      setDismissingIds((prev) => { const next = new Set(prev); next.delete(item.id); return next; });
                      onRead(item.id);
                    }
                  }}
                >
                  <ItemRow
                    item={item}
                    isRead={readIds.has(item.id)}
                    onRead={handleRead}
                    highlight={highlight}
                    mobile={mobile}
                  />
                </div>
              ))
            )}
          </div>
          {section.fetchStatus === "error" && (
            <div className={styles.reportRow}>
              {reported ? (
                <span className={styles.reportThanks}>Thanks! We're looking into it.</span>
              ) : (
                <button
                  type="button"
                  className={styles.reportBtn}
                  onClick={reportIssue}
                  disabled={reporting}
                >
                  {reporting ? "Reporting…" : "Report issue with feed"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
